import { GoogleLogin } from "@react-oauth/google";
import React, { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Divider,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link as MUILink,
} from "@mui/material";
import { PhoneIphone } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import {
  loginSendOtp,
  loginVerifyOtp,
  clearErrors,
  hydrateUserFromStorage,
  refreshTokenUserFromStorage,
  googleLogin,
} from "../redux/slices/userSlice";
import PageTitle from "../Components/PageTitle";

const Login = () => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { error, loading, isAuthenticated, token } = useSelector(
    (s) => s.user || {},
  );

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  // ✅ Send OTP
  const handleSendOtp = () => {
    if (!phone) return toast.error("Enter phone number");

    dispatch(loginSendOtp({ phone }))
      .unwrap()
      .then((msg) => {
        toast.success(msg);
        setOtpSent(true);
      })
      .catch((err) => toast.error(err));
  };

  // ✅ Verify OTP
  const handleVerifyOtp = () => {
    if (!otp) return toast.error("Enter OTP");

    dispatch(loginVerifyOtp({ phone, otp }))
      .unwrap()
      .then((data) => {
        toast.success("Login successful!");
        const user = data.user;

        if (user.role === 1) navigate("/admin-dashboard");
        else if (user.mode === "seller") navigate("/seller-dashboard");
        else if (user.mode === "buyer") navigate("/seller-list");
        else navigate("/select-mode");
      })
      .catch((err) => toast.error(err));
  };

  // ✅ Restore Session
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    const storedRefreshToken = localStorage.getItem("refreshToken");

    if (storedToken && storedUser && !token) {
      dispatch(
        hydrateUserFromStorage({
          token: storedToken,
          user: JSON.parse(storedUser),
        }),
      );
    }

    if (storedRefreshToken && storedUser && !token) {
      dispatch(
        refreshTokenUserFromStorage({
          refreshToken: storedRefreshToken,
          user: JSON.parse(storedUser),
        }),
      );
    }

    if (isAuthenticated && token && storedUser) {
      const user = JSON.parse(storedUser);

      if (user.role === 1) navigate("/admin-dashboard");
      else if (user.mode === "seller") navigate("/seller-dashboard");
      else if (user.mode === "buyer") navigate("/seller-list");
      else navigate("/select-mode");
    }
  }, [dispatch, navigate, isAuthenticated, token]);

  useEffect(() => {
    if (error) {
      dispatch(clearErrors());
    }
  }, [error, dispatch]);

  return (
    <Box>
      <PageTitle
        title="Login - BizBridge"
        description="Access your BizBridge account"
        url="/login"
      />

      {/* Breadcrumb */}
      <Breadcrumbs
        sx={{
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <MUILink
          component={Link}
          to="/"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Home
        </MUILink>
        <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
          Login
        </Typography>
      </Breadcrumbs>

      <Box
        sx={{
          minHeight: "calc(100vh - 120px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 420,
            background: "rgba(255,255,255,0.9)",
            borderRadius: "16px",
            padding: isSm ? 3 : 4,
            boxShadow:
              "0px 10px 25px rgba(0,0,0,0.08), 0px 4px 10px rgba(0,0,0,0.05)",
            border: "1px solid rgba(200,200,200,0.4)",
            textAlign: "center",
          }}
        >
          <Typography
            variant={isSm ? "h5" : "h4"}
            sx={{ fontWeight: 700, color: "#0b4064", mb: 1 }}
          >
            Welcome Back
          </Typography>

          <Typography sx={{ color: "#555", mb: 3 }}>
            Login with Phone OTP
          </Typography>

          {/* ✅ Phone Field FIXED */}
          <TextField
            fullWidth
            size="medium"
            placeholder="Enter Phone Number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            // sx={{ mb: 2 }}
            sx={{
              mb: 2,
              "& .MuiInputBase-root": {
                background: "#f5f5f5",
                borderRadius: "10px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIphone sx={{ color: "#0b4064" }} />
                </InputAdornment>
              ),
            }}
          />

          {/* ✅ OTP Field */}
          {otpSent && (
            <TextField
              fullWidth
              size="medium"
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              // sx={{ mb: 2 }}
              sx={{
                mb: 2,
                "& .MuiInputBase-root": {
                  background: "#f5f5f5",
                  borderRadius: "10px",
                },
              }}
            />
          )}

          <Button
            component={RouterLink}
            to="/forgot-password"
            sx={{
              textTransform: "none",
              color: "#0b4064",
              fontWeight: "600",
              fontSize: 14,
              mb: 2,
              alignSelf: "end",
              display: "block",
              width: "fit-content",
              marginLeft: "auto",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Forgot Password?
          </Button>

          {/* ✅ Button */}
          <Button
            fullWidth
            onClick={otpSent ? handleVerifyOtp : handleSendOtp}
            sx={{
              background: "#0b4064",
              padding: "12px",
              borderRadius: "10px",
              color: "#fff",
              fontWeight: "bold",
              fontSize: "16px",
              mb: 2,
              "&:hover": { background: "#09324d" },
            }}
          >
            {loading ? "Processing..." : otpSent ? "Verify OTP" : "Send OTP"}
          </Button>

          {/* Google Login */}
          <GoogleLogin
            onSuccess={(res) => {
              const id = res.credential;
              dispatch(googleLogin(id))
                .unwrap()
                .then((data) => {
                  toast.success("Google login successful!");
                  const user = data.user;

                  if (user.role === 1) navigate("/admin-dashboard");
                  else if (user.mode === "seller")
                    navigate("/seller-dashboard");
                  else if (user.mode === "buyer") navigate("/seller-list");
                  else navigate("/select-mode");
                })
                .catch((err) => toast.error(err));
            }}
            onError={() => toast.error("Google login failed!")}
          />

          <Divider sx={{ my: 3 }} />

          <Typography sx={{ color: "#555" }}>
            Don't have an account?{" "}
            <Button
              component={RouterLink}
              to="/register"
              sx={{ textTransform: "none", color: "#0b4064", fontWeight: 600 }}
            >
              Sign Up
            </Button>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
